import React, { useState, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { CaretDown, Check } from "@phosphor-icons/react";
import { CURRENCIES, currencyName, curCode, isUSD, isKnownCurrency } from "@/lib/currencies";
import api from "@/lib/api";

export function CurrencySelect({ value, onChange, testid, className = "", allowEmpty = false, exclude = [], flat = false, restrictTo }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const portalRef = useRef(null);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  // Resolve portal container ONCE on mount (stable ref, not recomputed per render).
  // If inside a Radix Dialog, portal into it so react-remove-scroll
  // doesn't block wheel/trackpad events on the popover's scroll container.
  const getPortalContainer = useCallback(() => {
    if (!portalRef.current && triggerRef.current) {
      portalRef.current = triggerRef.current.closest("[role='dialog']") || undefined;
    }
    return portalRef.current;
  }, []);

  const code = curCode(value);
  const excludeSet = new Set(exclude.map((c) => curCode(c)));
  const restrictSet = restrictTo ? new Set([...restrictTo].map((c) => curCode(c))) : null;
  const isAllowed = (c) => !excludeSet.has(c) && (!restrictSet || restrictSet.has(c));

  // Build pinned list: USD first, then tenant's chosen currencies (deduped, valid ISO only)
  const tenantCurrencies = (settings?.currencies || []).map((c) => curCode(c)).filter(isKnownCurrency);
  const pinned = ["USD", ...tenantCurrencies.filter((c) => !isUSD(c))];
  const pinnedSet = new Set(pinned);

  const pinnedRows = CURRENCIES.filter((c) => pinnedSet.has(c.code) && isAllowed(c.code))
    .sort((a, b) => pinned.indexOf(a.code) - pinned.indexOf(b.code));
  const rest = CURRENCIES.filter((c) => !pinnedSet.has(c.code) && isAllowed(c.code));

  // flat mode: single alphabetical list (no group headings)
  const allSorted = flat
    ? CURRENCIES.filter((c) => isAllowed(c.code)).sort((a, b) => a.code.localeCompare(b.code))
    : null;

  const pick = (c) => { onChange(c); setOpen(false); };
  const label = code ? (currencyName(code) !== code ? `${code} — ${currencyName(code)}` : code) : (allowEmpty ? "—" : "Select currency");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" ref={triggerRef} data-enter-field data-testid={testid}
          className={`flex h-10 w-full items-center justify-between rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30 ${className}`}>
          <span className="truncate">{label}</span>
          <CaretDown size={14} weight="bold" className="ml-1 shrink-0 text-[#6b7280]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,460px)] p-0" container={open ? getPortalContainer() : undefined} data-testid={testid ? `${testid}-popover` : undefined}>
        <Command>
          <CommandInput placeholder="Type a code, currency, or country" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-[260px]">
            <CommandEmpty>No matching currency.</CommandEmpty>
            {allowEmpty && (
              <CommandItem value="none —" onSelect={() => pick("")}>— None</CommandItem>
            )}
            {flat ? (
              <CommandGroup>
                {allSorted.map((c) => <Row key={c.code} c={c} active={c.code === code} onPick={() => pick(c.code)} />)}
              </CommandGroup>
            ) : (
              <>
                <CommandGroup heading="Your currencies">
                  {pinnedRows.map((c) => <Row key={c.code} c={c} active={c.code === code} onPick={() => pick(c.code)} />)}
                </CommandGroup>
                <CommandGroup heading="All other currencies">
                  {rest.map((c) => <Row key={c.code} c={c} active={c.code === code} onPick={() => pick(c.code)} />)}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Row({ c, active, onPick }) {
  return (
    <CommandItem value={`${c.code} ${c.name} ${c.country}`} onSelect={onPick} className="flex items-start justify-between gap-2">
      <span className="min-w-0"><b>{c.code}</b> <span className="text-[#6b7280]">{c.name}</span>{c.country && <span className="text-[#9CA3AF]"> · {c.country}</span>}</span>
      {active && <Check size={14} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />}
    </CommandItem>
  );
}
